import { api } from "./client";
import type { ApiResponse, PaginatedResponse } from "./types";

export interface Institution {
  id: string;
  name: string;
  slug: string;
  displayName: string | null;
  description: string | null;
  tagline: string | null;
  institutionType: string;
  religion: string;
  tradition: string | null;
  city: string;
  state: string;
  country: string;
  phone: string | null;
  email: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  avatarUrl: string | null;
  verificationStatus: "draft" | "pending" | "approved" | "rejected" | "suspended";
  verifiedAt: string | null;
  isFeatured: boolean;
  followerCount: number;
  eventCount: number;
  totalAttendance: number;
  volunteerCount: number;
  yearEstablished: number | null;
  createdAt: string;
  updatedAt: string;
  viewerMembership?: { role: string };
}

export interface ListInstitutionsParams {
  type?: string;
  religion?: string;
  city?: string;
  state?: string;
  q?: string;
  verificationStatus?: string;
  page?: number;
  limit?: number;
  sort?: "created_at" | "follower_count" | "event_count" | "name";
  order?: "asc" | "desc";
}

export function listInstitutions(params?: ListInstitutionsParams) {
  return api.get<{
    success: true;
    data: { items: Institution[]; meta: PaginatedResponse<unknown>["meta"] };
  }>("/institutions", params as Record<string, string | number | boolean | undefined>);
}

export function getInstitution(idOrSlug: string) {
  return api.get<ApiResponse<Institution>>(`/institutions/${idOrSlug}`);
}

export interface CreateInstitutionInput {
  name: string;
  institutionType: string;
  religion: string;
  tradition?: string;
  city: string;
  state: string;
  country?: string;
  phone?: string;
  email?: string;
  websiteUrl?: string;
  description?: string;
  yearEstablished?: number;
}

export function createInstitution(input: CreateInstitutionInput) {
  return api.post<ApiResponse<Institution>>("/institutions", input);
}

export function followInstitution(id: string) {
  return api.post<ApiResponse<unknown>>(`/institutions/${id}/follow`, {});
}

export function unfollowInstitution(id: string) {
  return api.delete<ApiResponse<unknown>>(`/institutions/${id}/follow`);
}

// Each row is an InstitutionFollower record — the institution itself is nested
// under `.institution`, NOT flattened onto the row.
export interface FollowedInstitution {
  id: string;
  createdAt?: string;
  notifyEvents?: boolean;
  institution: Institution;
}

export function getFollowedInstitutions() {
  return api.get<
    ApiResponse<{ items: FollowedInstitution[]; meta: PaginatedResponse<unknown>["meta"] }>
  >("/users/me/followed-institutions");
}

export function submitInstitutionVerification(id: string) {
  return api.post<ApiResponse<unknown>>(
    `/institutions/${id}/submit-verification`,
    {},
  );
}

// ─── Verification documents ────────────────────────────────────────
// Submit-verification hard-requires ≥1 document; this is how they get
// attached (URL-first, same pattern as photos).

export type InstitutionDocumentType =
  | "irs_501c3_letter"
  | "ein_confirmation"
  | "state_registration"
  | "articles_of_incorporation"
  | "board_resolution"
  | "utility_bill"
  | "photo_id_contact"
  | "institution_exterior"
  | "institution_interior"
  | "other";

export function addInstitutionDocument(
  id: string,
  body: { documentType: InstitutionDocumentType; fileUrl: string; fileName?: string },
) {
  return api.post<ApiResponse<unknown>>(`/institutions/${id}/documents`, body);
}

// ============================================================================
// Running an institution — the admin side of a temple, gurdwara or mosque.
//
// All of it is behind membership: `requireInstitutionMember` refuses anyone
// without an active org_members row, so on an institution you do not help run,
// every call here answers "You are not a member of this institution".
//
// Shapes read from docs/api/openapi.json. Two of them are easy to get wrong:
//
//   · `/institutions/mine` returns a BARE ARRAY as its data, not a wrapper.
//     Everything else here nests under `items`.
//   · The legal record NEVER returns the tax id. It returns `einLast4` and
//     `hasEin`. Writing sends the full number; reading cannot get it back,
//     because it is encrypted at rest and there is deliberately no read path.
// ============================================================================

export const SCHEDULE_DAYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "daily", "special",
] as const;

export const LEGAL_ENTITY_TYPES = [
  "nonprofit_501c3", "nonprofit_other", "llc", "corporation",
  "unincorporated_association", "individual",
] as const;

export interface MyInstitutionRef {
  id: string;
  name: string;
  slug: string;
  city: string;
  state: string;
  religion: string;
  verificationStatus: string;
  myRole: string;
  /**
   * What this person may do. HIDE what is false rather than rendering a
   * control the server is going to refuse — the spec says so explicitly.
   */
  can: {
    editProfile: boolean;
    createEvents: boolean;
    manageVolunteers: boolean;
    uploadDocs: boolean;
    inviteAdmins: boolean;
  };
}

export interface InstitutionDashboard {
  institution: {
    id: string;
    name: string;
    followerCount: number;
    eventCount: number;
    volunteerCount: number;
    totalAttendance: number;
    verificationStatus: string;
  };
  stats: {
    followerCount: number;
    upcomingEventsCount: number;
    pendingVolunteers: number;
    openOpportunities: number;
  };
}

export interface InstitutionMember {
  id: string;
  userId: string;
  role: string;
  title: string | null;
  joinedAt: string;
  /** Carries EMAIL — an admin-only surface, like the event attendee list. */
  user: { id: string; name: string; email: string; avatarUrl: string | null };
}

export interface InstitutionSchedule {
  id: string;
  dayType: string;
  activityName: string;
  startTime: string;
  endTime: string | null;
  description: string | null;
  language: string | null;
  isPublic: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface InstitutionPhoto {
  id: string;
  photoUrl: string;
  thumbnailUrl: string | null;
  caption: string | null;
  category: string | null;
  isPrimary: boolean;
  sortOrder: number;
}

export interface InstitutionFollower {
  id: string;
  userId: string;
  notifyEvents: boolean;
  notifyAnnouncements: boolean;
  notifyVolunteerOpps: boolean;
  user: { id: string; name: string; city: string | null; state: string | null };
}

export interface LegalIdentityState {
  /** False when the server holds no encryption key — EIN writes are SKIPPED. */
  einCaptureEnabled: boolean;
  legal: {
    legalEntityName: string | null;
    entityType: string | null;
    /** Last 4 only. The full tax id is never returned by anything. */
    einLast4: string | null;
    hasEin: boolean;
    stateOfRegistration: string | null;
    incorporationYear: number | null;
    registeredAddress: string | null;
    primaryContactName: string | null;
    primaryContactEmail: string | null;
    primaryContactPhone: string | null;
  } | null;
}

/** BARE ARRAY, not `{ items: [...] }`. The odd one out in this module. */
export function getMyInstitutions() {
  return api.get<ApiResponse<MyInstitutionRef[]>>("/institutions/mine");
}

export function getInstitutionDashboard(id: string) {
  return api.get<ApiResponse<InstitutionDashboard>>(`/institutions/${id}/dashboard`);
}

export function listInstitutionAdmins(id: string) {
  return api.get<ApiResponse<{ items: InstitutionMember[] }>>(`/institutions/${id}/admins`);
}

export function inviteInstitutionAdmin(id: string, email: string, role: string) {
  return api.post<ApiResponse<unknown>>(`/institutions/${id}/admins/invite`, { email, role });
}

export function updateInstitutionAdmin(id: string, memberId: string, input: Record<string, unknown>) {
  return api.patch<ApiResponse<unknown>>(`/institutions/${id}/admins/${memberId}`, input);
}

export function removeInstitutionAdmin(id: string, memberId: string) {
  return api.delete<ApiResponse<void>>(`/institutions/${id}/admins/${memberId}`);
}

export function listInstitutionSchedules(id: string) {
  return api.get<ApiResponse<{ items: InstitutionSchedule[] }>>(`/institutions/${id}/schedules`);
}

export function createInstitutionSchedule(
  id: string,
  input: {
    dayType: string;
    activityName: string;
    /** Strictly HH:MM — the server pattern-matches it. */
    startTime: string;
    endTime?: string;
    description?: string;
    language?: string;
    isPublic?: boolean;
  },
) {
  return api.post<ApiResponse<InstitutionSchedule>>(`/institutions/${id}/schedules`, input);
}

export function updateInstitutionSchedule(
  id: string,
  scheduleId: string,
  input: Partial<{ activityName: string; startTime: string; isPublic: boolean; isActive: boolean }>,
) {
  return api.patch<ApiResponse<InstitutionSchedule>>(
    `/institutions/${id}/schedules/${scheduleId}`,
    input,
  );
}

export function deleteInstitutionSchedule(id: string, scheduleId: string) {
  return api.delete<ApiResponse<void>>(`/institutions/${id}/schedules/${scheduleId}`);
}

export function listInstitutionPhotos(id: string) {
  return api.get<ApiResponse<{ items: InstitutionPhoto[] }>>(`/institutions/${id}/photos`);
}

export function createInstitutionPhoto(
  id: string,
  input: { photoUrl: string; caption?: string; category?: string; isPrimary?: boolean },
) {
  return api.post<ApiResponse<InstitutionPhoto>>(`/institutions/${id}/photos`, input);
}

export function deleteInstitutionPhoto(id: string, photoId: string) {
  return api.delete<ApiResponse<void>>(`/institutions/${id}/photos/${photoId}`);
}

export function listInstitutionFollowers(id: string, params?: { page?: number; limit?: number }) {
  return api.get<
    ApiResponse<{ items: InstitutionFollower[]; meta: { page: number; limit: number; total: number; totalPages: number } }>
  >(`/institutions/${id}/followers`, params as Record<string, string | number | boolean | undefined>);
}

export function getInstitutionLegal(id: string) {
  return api.get<ApiResponse<LegalIdentityState>>(`/institutions/${id}/legal`);
}

export function updateInstitutionLegal(
  id: string,
  input: Partial<{
    legalEntityName: string | null;
    entityType: string | null;
    /** Write-only. Sent whole, returned never. */
    ein: string | null;
    stateOfRegistration: string | null;
    incorporationYear: number | null;
    primaryContactName: string | null;
    primaryContactEmail: string | null;
  }>,
) {
  return api.put<ApiResponse<LegalIdentityState>>(`/institutions/${id}/legal`, input);
}
